import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Github, Twitter, Instagram, Mail, CheckCircle, AlertCircle, Send, Sparkles, MapPin } from 'lucide-react';

const Footer: React.FC = () => {
    const location = useLocation();
    const [email, setEmail] = useState<string>('');
    const [isSubscribing, setIsSubscribing] = useState<boolean>(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [isMobile, setIsMobile] = useState<boolean>(false);

    // 모바일 환경 감지
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768); // md 브레이크포인트 기준
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // 모바일 환경에서 홈페이지가 아닌 경우 렌더링하지 않음
    if (isMobile && location.pathname !== '/') {
        return null;
    }

    const handleNewsletterSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setSubscriptionStatus('error');
            return;
        }

        setIsSubscribing(true);
        setSubscriptionStatus('idle');

        try {
            // 실제 뉴스레터 구독 API 호출 (여기서는 시뮬레이션)
            await new Promise(resolve => setTimeout(resolve, 1500));

            setSubscriptionStatus('success');
            setEmail('');

            // 3초 후 상태 초기화
            setTimeout(() => {
                setSubscriptionStatus('idle');
            }, 3000);
        } catch (error) {
            setSubscriptionStatus('error');
        } finally {
            setIsSubscribing(false);
        }
    };

    const footerLinks = {
        product: [
            { name: 'Features', href: '/#features' },
            { name: 'Analytics', href: '/analytics' },
            { name: 'Dashboard', href: '/dashboard' }
        ],
        company: [

            { name: 'Careers', href: '/careers' },
            { name: 'Contact', href: '/contact' }
        ],
        support: [

            { name: 'Privacy Policy', href: '/privacy' },
            { name: 'Terms of Service', href: '/terms' },
            { name: 'Status', href: '/status' }
        ]
    };



    const CompanyLogo: React.FC = () => (
        <img
            src="https://ik.imagekit.io/u9hycyuth/%E1%84%89%E1%85%B3%E1%84%8F%E1%85%B3%E1%84%85%E1%85%B5%E1%86%AB%E1%84%89%E1%85%A3%E1%86%BA%202025-06-13%20%E1%84%8B%E1%85%A9%E1%84%92%E1%85%AE%209.14.59-Photoroom.png?updatedAt=1749816936129"
            alt="SocialDataLab"
            className="w-8 h-8 object-contain"
        />
    );

    return (
        <footer className="relative z-20 bg-black border-t border-[#262626] overflow-hidden">
            {/* Glass morphism overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#101010]/50 to-black/90 backdrop-blur-xl"></div>

            {/* Accent gradient effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#3182F6]/5 blur-[100px] sm:blur-[150px] rounded-full"></div>
                <div className="absolute bottom-0 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#3182F6]/5 blur-[100px] sm:blur-[150px] rounded-full"></div>
            </div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Main Footer Content */}
                <div className="py-8 sm:py-12 lg:py-16">
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
                        {/* Brand Section */}
                        <div className="col-span-2 sm:col-span-2 md:col-span-4 lg:col-span-2">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true }}
                            >
                                <Link to="/" className="flex items-center gap-3 mb-6 group">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-[#3182F6]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                                        <div className="relative bg-[#181818] p-2.5 rounded-2xl border border-[#262626] group-hover:border-[#3182F6]/30 transition-all duration-300">
                                            <CompanyLogo />
                                        </div>
                                    </div>
                                    <span className="text-xl sm:text-2xl font-bold text-white group-hover:text-[#3182F6] transition-colors duration-300">
                                        SocialDataLab
                                    </span>
                                </Link>
                                <p className="text-[#A8A8A8] text-sm leading-relaxed max-w-xs mb-6">
                                    {/* 2026-10-28 대표 "고쳐" — 예전: Instagram 프로필 방문자를 추적하는 고급 분석 기술로 누가 당신의 피드에 관심을 가지고 있는지 알아보세요. */}
                                    <span className="text-white font-medium">내 인스타그램을 향한 관심</span>을 공개 데이터로 분석해 드려요.
                                </p>


                            </motion.div>
                        </div>

                        {/* Product Links */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                            className="col-span-1"
                        >
                            <h3 className="text-xs font-semibold text-[#777777] uppercase tracking-wider mb-4">
                                Product
                            </h3>
                            <ul className="space-y-3">
                                {footerLinks.product.map((link) => (
                                    <li key={link.name}>
                                        <Link
                                            to={link.href}
                                            className="text-[#A8A8A8] hover:text-white text-sm inline-flex items-center gap-1.5 group transition-colors duration-200"
                                        >
                                            <span>{link.name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Company Links - Hidden on mobile */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="col-span-1 hidden sm:block"
                        >
                            <h3 className="text-xs font-semibold text-[#777777] uppercase tracking-wider mb-4">
                                Company
                            </h3>
                            <ul className="space-y-3">
                                {footerLinks.company.map((link) => (
                                    <li key={link.name}>
                                        <Link
                                            to={link.href}
                                            className="text-[#A8A8A8] hover:text-white text-sm inline-flex items-center gap-1.5 group transition-colors duration-200"
                                        >
                                            <span>{link.name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Support Links */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            viewport={{ once: true }}
                            className="col-span-1"
                        >
                            <h3 className="text-xs font-semibold text-[#777777] uppercase tracking-wider mb-4">
                                Support
                            </h3>
                            <ul className="space-y-3">
                                {footerLinks.support.map((link) => (
                                    <li key={link.name}>
                                        <Link
                                            to={link.href}
                                            className="text-[#A8A8A8] hover:text-white text-sm inline-flex items-center gap-1.5 group transition-colors duration-200"
                                        >
                                            <span>{link.name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>


                    </div>
                </div>

                {/* Bottom Bar */}
                <motion.div
                    className="border-t border-[#262626] py-6 sm:py-8"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    viewport={{ once: true }}
                >


                    {/* 사업자 정보 */}
                    <div className="mb-5 text-center lg:text-left text-[11px] sm:text-xs text-[#777777] leading-relaxed space-y-0.5">
                        <p>상호: 다니엘퍼스트(DANIEL1st) | 대표자: 임규빈 | 사업자등록번호: 603-17-50971</p>
                        <p>사업장 소재지: 부산광역시 해운대구 센텀중앙로 97, A동 3층 304-1호(재송동, 센텀스카이비즈)</p>
                        {/* 2026-09-16 다니엘 결정: 문의는 문의하기 페이지에서만 접수 — 이메일 주소 노출 제거 */}
                        <p>문의: <Link to="/contact" className="underline hover:text-[#4FC3F7]">문의하기 페이지</Link>에서만 접수합니다</p>
                    </div>

                    {/* Copyright and Links */}
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-col items-center gap-1 text-center text-[#777777] text-xs sm:text-sm lg:items-start">
                            <motion.span
                                className="tracking-wide uppercase text-[11px] sm:text-xs text-[#4FC3F7]"
                                initial={{ opacity: 0, y: 4 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                                viewport={{ once: true }}
                            >
                                SocialDataLab
                            </motion.span>
                            <span className="text-[11px] sm:text-xs text-[#A8A8A8]">
                                © 2025 SocialDataLab. All rights reserved.
                            </span>

                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs">
                            {[
                                { name: 'Privacy', href: '/privacy' },
                                { name: 'Terms', href: '/terms' },
                                { name: 'Home', href: '/refund' },
                                { name: 'Contact', href: '/contact' }
                            ].map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.href}
                                    className="text-xs text-[#A8A8A8] hover:text-white transition-colors duration-200 relative group"
                                >
                                    {link.name}
                                    <span className="absolute -bottom-0.5 left-0 w-0 h-[1px] bg-[#3182F6] group-hover:w-full transition-all duration-300"></span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </footer>
    );
};

export default Footer; 
